import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { useNavigate } from "@tanstack/react-router"
import { TFunction } from "i18next"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import { requiredRule, validEmailRule } from "@/utilities/form"

import styles from "./index.module.css"

export const Route = createFileRoute("/auth/sign-in/")({
  component: RouteComponent,
})

interface SignInFormSchema {
  email: string
  password: string
}

function logMutationError(
  error: any,
  context: {
    flow: string
    email?: string
    qrLookupID?: string
    additionalData?: Record<string, any>
  }
) {
  Sentry.captureException(error, {
    extra: {
      email: context.email,
      qrLookupID: context.qrLookupID,
      originalError: error.message,
      ...context.additionalData,
    },
    tags: {
      section: "auth",
      flow: context.flow,
    },
  })
}

function useSignInMutation({
  form,
  redirect,
  qrLookupID,
  t,
}: {
  form: ReturnType<typeof useForm<SignInFormSchema>>
  redirect?: string
  qrLookupID?: string
  t: TFunction
}) {
  const router = useRouter()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async (data: SignInFormSchema) => {
      return api.auth.signIn({
        username: data.email,
        password: data.password,
      })
    },
    onSuccess: (result, variables) => {
      if (result.isSignedIn) {
        if (redirect) {
          router.history.push(redirect)
        } else if (qrLookupID) {
          navigate({ to: "/qr/$qrLookupID/welcome", params: { qrLookupID } })
        } else {
          navigate({ to: "/" })
        }
      } else if (result.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        navigate({
          to: "/auth/verify",
          search: {
            email: variables.email,
            context: "sign-in",
            qrLookupID,
            redirect,
          },
        })
      } else {
        form.setError("root", {
          message:
            "Additional authentication step required. Please contact support.",
        })
      }
    },
    onError: (err: any, variables) => {
      logMutationError(err, {
        flow: "sign-in",
        email: variables.email,
        qrLookupID,
        additionalData: { redirect },
      })
      handleAuthError(err, form, t)
    },
  })
}

function RouteComponent() {
  const { redirect, qrLookupID } = Route.useSearch()
  const { t } = useTranslation()

  const form = useForm<SignInFormSchema>({
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onBlur",
  })

  const signInMutation = useSignInMutation({ form, redirect, qrLookupID, t })

  const onSubmit = (data: SignInFormSchema) => {
    form.clearErrors("root")
    signInMutation.mutate(data)
  }

  return (
    <>
      <Heading level={1} as={1}>
        {t("auth.signIn.heading")}
      </Heading>

      <Text level={2} style={{ marginTop: "var(--space-2)" }}>
        {t("auth.signIn.description")}
      </Text>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset
          style={{ width: "100%", minWidth: 0 }}
          disabled={signInMutation.isPending}
        >
          <Controller<SignInFormSchema>
            name="email"
            control={form.control}
            rules={{
              required: requiredRule(t("errors.common.required")),
              pattern: validEmailRule(t("errors.common.validEmail")),
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>{t("auth.signIn.email")}</Label>
                <TextField.Input
                  innerRef={ref}
                  type="email"
                  placeholder="alex.smith@example.com"
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller<SignInFormSchema>
            name="password"
            control={form.control}
            rules={{ required: requiredRule(t("errors.common.required")) }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <div className={styles.passwordLabelRow}>
                  <Label>{t("auth.signIn.password")}</Label>
                  <LinkButton
                    hue="primary"
                    presentation="link"
                    to="/auth/forgot-password"
                    search={{ qrLookupID, redirect }}
                    style={{
                      fontSize: "var(--font-size-2)",
                      fontWeight: "400",
                      textDecorationLine: "underline",
                    }}
                  >
                    {t("auth.signIn.troubleSigningIn")}
                  </LinkButton>
                </div>
                <TextField.Password innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button
            hue="secondary"
            variant="default"
            type="submit"
            isPending={signInMutation.isPending}
          >
            {t("auth.signIn.submitButton")}
          </Button>
        </fieldset>
      </form>

      {/* Create Account Link */}
      <Text level={2} className="center">
        {t("auth.signIn.noAccount")}{" "}
        <LinkButton
          hue="primary"
          presentation="link"
          to="/auth/create-account"
          search={{ qrLookupID, redirect }}
          style={{ textDecorationLine: "underline" }}
        >
          {t("auth.signIn.signUpLink")}
        </LinkButton>
        .
      </Text>
    </>
  )
}
